import { LayerExtension } from "@deck.gl/core";
import type { IPixPolygon, IPixPolygonPattern } from "./types";

export function patternDimensions(pattern: IPixPolygonPattern) {
  const spacing = Number.isFinite(pattern.spacing) ? Math.max(4, pattern.spacing!) : 14;
  const lineWidth = Number.isFinite(pattern.lineWidth) ? Math.min(spacing / 2, Math.max(0.5, pattern.lineWidth!)) : 1.5;
  return { spacing, lineWidth };
}

/** Batch by geometry only; each polygon retains its own color and opacity. */
export function groupPatternPolygons<T extends IPixPolygon>(polygons: T[]) {
  const groups = new Map<string, { dimensions: ReturnType<typeof patternDimensions>; polygons: T[] }>();
  for (const polygon of polygons) {
    if (!polygon.pattern) continue;
    const dimensions = patternDimensions(polygon.pattern);
    const key = `${dimensions.spacing}:${dimensions.lineWidth}`;
    let group = groups.get(key);
    if (!group) {
      group = { dimensions, polygons: [] };
      groups.set(key, group);
    }
    group.polygons.push(polygon);
  }
  return [...groups.entries()];
}

/** Screen-space hatching: consistent CSS-pixel density at every zoom and DPI.
 * Applied to a non-pickable overlay, leaving the underlying polygon fill intact.
 */
export class PolygonPatternExtension extends LayerExtension<{ spacing: number; lineWidth: number }> {
  static extensionName = "PolygonPatternExtension";

  getShaders(_extension: this) {
    const { spacing, lineWidth } = _extension.opts;
    return {
      inject: {
        "vs:#decl": "out float polygonPatternPixelRatio;",
        "fs:#decl": "in float polygonPatternPixelRatio;",
        "vs:DECKGL_FILTER_COLOR": "polygonPatternPixelRatio = project.devicePixelRatio;",
        "fs:DECKGL_FILTER_COLOR": `
          vec2 patternPixel = gl_FragCoord.xy / polygonPatternPixelRatio;
          float patternSpacing = ${spacing.toFixed(4)};
          float patternPhase = mod((patternPixel.x + patternPixel.y) * 0.70710678, patternSpacing);
          float patternDistance = min(patternPhase, patternSpacing - patternPhase);
          float patternCoverage = 1.0 - smoothstep(${(lineWidth / 2).toFixed(4)} - 0.5, ${(lineWidth / 2).toFixed(4)} + 0.5, patternDistance);
          color.a *= patternCoverage;
        `,
      },
    };
  }
}
